import { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import ReactSelect from 'react-select';
import { API_URL } from '../../../api/api';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const token = localStorage.getItem('token');
const MAX_VISIBLE = 3;

const RoleAccessLayer = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [roleName, setRoleName] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Get auth headers
    const getHeaders = () => {
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };
    };

    useEffect(() => {
        fetchRoles();
        fetchPermissions();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/roles`, {
                headers: getHeaders(),
            });
            console.log('Roles fetched:', data);
            setRoles(data);
        } catch (error) {
            console.error('Error fetching roles:', error);
            Swal.fire('Error', 'Failed to fetch roles', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/roles/permissions`, {
                headers: getHeaders(),
            });
            console.log('Permissions fetched:', data);
            setPermissions(data);
        } catch (err) {
            console.error('Error fetching permissions:', err);
            Swal.fire('Error', 'Failed to fetch permissions', 'error');
        }
    };

    const resetForm = () => {
        setRoleName('');
        setSelectedPermissions([]);
        setIsEditing(false);
        setEditingRoleId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!roleName.trim()) {
            return Swal.fire('Required', 'Role name is required', 'warning');
        }

        try {
            let roleId;

            if (isEditing) {
                // Update existing role
                await axios.put(
                    `${API_URL}/roles/${editingRoleId}`,
                    {
                        name: roleName,
                        guard_name: 'web'
                    },
                    { headers: getHeaders() }
                );
                roleId = editingRoleId;
            } else {
                // Create new role
                const roleRes = await axios.post(
                    `${API_URL}/roles`,
                    { name: roleName },
                    { headers: getHeaders() }
                );
                roleId = roleRes.data.id;
            }

            // Update permissions for the role
            if (selectedPermissions.length > 0) {
                await axios.put(
                    `${API_URL}/roles/${roleId}/permissions`,
                    { permissions: selectedPermissions },
                    { headers: getHeaders() }
                );
            }

            Swal.fire(
                'Success',
                `Role ${isEditing ? 'updated' : 'created'} successfully`,
                'success'
            );

            resetForm();
            setShowModal(false);
            fetchRoles(); // Refresh the list
        } catch (error) {
            console.error('Error saving role:', error);

            // Extract error message
            let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} role`;
            if (error.response?.data?.errors?.name) {
                errorMessage = error.response.data.errors.name[0];
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = 'Unauthorized. Please check your authentication.';
            }

            Swal.fire('Error', errorMessage, 'error');
        }
    };

    const handleEdit = (role) => {
        setIsEditing(true);
        setEditingRoleId(role.id);
        setRoleName(role.name);
        // Extract permission IDs from role.permissions array
        const permissionIds = role.permissions ? role.permissions.map(p => p.id) : [];
        setSelectedPermissions(permissionIds);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Delete role?',
            text: 'This cannot be undone',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!confirm.isConfirmed) return;

        try {
            await axios.delete(`${API_URL}/roles/${id}`, {
                headers: getHeaders(),
            });
            Swal.fire('Deleted', 'Role has been deleted', 'success');
            fetchRoles();
        } catch (error) {
            console.error('Error deleting role:', error);
            Swal.fire('Error', 'Failed to delete role', 'error');
        }
    };

    // Prepare React Select options for permissions
    const permissionOptions = permissions.map((p) => ({
        value: p.id,
        label: p.name,
    }));

    // Selected permissions for React Select value
    const selectedOptions = permissionOptions.filter((opt) =>
        selectedPermissions.includes(opt.value)
    );

    return (
        <>
            <div className="card h-100 p-0 radius-12 max-w-7xl mx-auto mt-6">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <h5 className="text-xl fw-semibold text-secondary-light mb-0">
                        Role Management
                    </h5>
                    <button
                        type="button"
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}
                        disabled={loading}
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New Role
                    </button>
                </div>
                <div className="card-body p-24">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 text-muted">Loading roles...</p>
                        </div>
                    ) : (
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Role Name</th>
                                        <th>Guard Name</th>
                                        <th>Permissions</th>
                                        <th className="text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roles.length ? (
                                        roles.map((role) => (
                                            <tr key={role.id}>
                                                <td className="align-middle">{role.id}</td>
                                                <td className="align-middle">{role.name}</td>
                                                <td className="align-middle">{role.guard_name || 'web'}</td>
                                                <td className="align-middle">
                                                    {role.permissions && role.permissions.length > 0 ? (
                                                        <>
                                                            {role.permissions.slice(0, MAX_VISIBLE).map((p) => (
                                                                <span
                                                                    key={p.id}
                                                                    className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 me-1 mb-1"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                >
                                                                    {p.name}
                                                                </span>
                                                            ))}
                                                            {role.permissions.length > MAX_VISIBLE && (
                                                                <>
                                                                    <span
                                                                        id={`perm-tooltip-target-${role.id}`}
                                                                        className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 cursor-pointer"
                                                                        style={{ fontSize: '0.75rem' }}
                                                                    >
                                                                        +{role.permissions.length - MAX_VISIBLE} more
                                                                    </span>
                                                                    <Tooltip
                                                                        anchorId={`perm-tooltip-target-${role.id}`}
                                                                        place="top"
                                                                        content={role.permissions
                                                                            .slice(MAX_VISIBLE)
                                                                            .map((p) => p.name)
                                                                            .join(', ')}
                                                                        className="z-9999"
                                                                    />
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                                                            No permissions
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="d-flex align-items-center gap-10 justify-content-center">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary btn-icon"
                                                            onClick={() => handleEdit(role)}
                                                            title="Edit Role"
                                                        >
                                                            <Icon icon="lucide:edit" className="icon-sm" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger btn-icon"
                                                            onClick={() => handleDelete(role.id)}
                                                            title="Delete Role"
                                                            disabled={role.name === 'admin' || role.name === 'Admin'}
                                                        >
                                                            <Icon icon="fluent:delete-24-regular" className="icon-sm" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center text-muted py-12">
                                                No roles found. Create your first role!
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for Add/Edit Role */}
            {showModal && (
                <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    aria-modal="true"
                    role="dialog"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                >
                    <div
                        className="modal-dialog modal-lg modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content radius-16 bg-base">
                            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
                                <h5 className="modal-title fs-5 fw-semibold">
                                    {isEditing ? 'Edit Role' : 'Add New Role'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        resetForm();
                                        setShowModal(false);
                                    }}
                                    aria-label="Close"
                                />
                            </div>
                            <div className="modal-body p-24">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Role Name *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control radius-8"
                                            placeholder="Enter Role Name (e.g., Manager, Editor)"
                                            value={roleName}
                                            onChange={(e) => setRoleName(e.target.value)}
                                            autoFocus
                                            required
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                            Assign Permissions *
                                        </label>
                                        <ReactSelect
                                            options={permissionOptions}
                                            value={selectedOptions}
                                            onChange={(selected) =>
                                                setSelectedPermissions(selected ? selected.map((s) => s.value) : [])
                                            }
                                            isMulti
                                            placeholder="Select permissions..."
                                            classNamePrefix="react-select"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    borderRadius: '8px',
                                                    borderColor: '#dee2e6',
                                                    minHeight: '45px',
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    zIndex: 9999
                                                }),
                                            }}
                                            noOptionsMessage={() => 'No permissions found'}
                                        />
                                        <div className="form-text mt-2">
                                            Selected: {selectedPermissions.length} permission(s)
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary px-40 py-11 radius-8"
                                            onClick={() => {
                                                resetForm();
                                                setShowModal(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary px-48 py-12 radius-8"
                                            disabled={!roleName.trim() || selectedPermissions.length === 0}
                                        >
                                            {isEditing ? 'Update Role' : 'Save Role'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RoleAccessLayer;